import React, { useState, useEffect, useRef, useMemo } from 'react';
import GeneralSearchPanel from './SearchPanel/GeneralSearchPanelFigma';
import ColumnsMenu from './ColumnsMenu';
import debounce from 'lodash.debounce';
import ShownPanelsEnum from '../Enums/ShownPanelsEnum';
import RoleService from '../Services/roleService';

// Counts panel filters that differ from a fresh searchParamsType instance.
// Defensive: returns 0 (badge hidden) for any missing/odd input — never throws.
function countActiveFilters(item, searchParamsType) {
  if (!item || !searchParamsType || !Array.isArray(searchParamsType.fieldsInSearchBox)) return 0;
  // Pass `true` so the base constructor builds TRUE defaults and SKIPS loading the
  // URL query into the instance (SearchParamsBase reads window.location otherwise).
  // Without it, applied URL filters land in "defaults" too and cancel out — the
  // badge would never count a deep-linked/applied filter.
  let defaults;
  try {
    defaults = new searchParamsType(true);
  } catch (e) {
    return 0;
  }
  // Normalize to a comparable scalar so a field counts as "active" only when its
  // value actually DIFFERS from the default — value-based, not reference-based.
  // Empty-ish values (null/undefined/''/[]) collapse to ''; Dates compare by time
  // (so two "indefinite date" sentinels are equal); other objects by JSON. Without
  // this, the 6 date fields (each a fresh Date sentinel) failed === and showed as 6.
  const normalize = (v) => {
    if (v === null || v === undefined || v === '') return '';
    if (v instanceof Date) return v.getTime();
    if (Array.isArray(v) && v.length === 0) return '';
    if (typeof v === 'object') {
      try {
        return JSON.stringify(v);
      } catch (e) {
        return '';
      }
    }
    return v;
  };
  return searchParamsType.fieldsInSearchBox.reduce((count, f) => {
    const name = f && f.fieldName;
    if (!name || name === 'SingleSearchLineText') return count;
    return normalize(item[name]) === normalize(defaults[name]) ? count : count + 1;
  }, 0);
}

const ButtonForGeneralSearchStrip = (
  disabled,
  onClick,
  tooltipTitle,
  iconClassName,
  roleKey = null,
  extraClass = ''
) => {
  // The user should access if there is no roleKey prop or if there is a roleKey prop and the user has the role
  const doesUserHaveAccess = !roleKey || (roleKey && RoleService.CheckUserPermission('ButtonRowButton', roleKey));

  return (
    <button
      type='button'
      className={`icon-btn ${extraClass}`.trim()}
      disabled={disabled || !doesUserHaveAccess}
      onClick={onClick}
      title={tooltipTitle}
    >
      <i className={iconClassName} />
    </button>
  );
};

export default function GeneralSearchStrip(props) {
  const [searchItem, setSearchItem] = useState(() => ({ ...props.searchItem }));
  const [showFilter, setShowFilter] = useState(false);
  const [singleSearchLineText, setSingleSearchLineText] = useState(
    props.searchItem && props.searchItem.SingleSearchLineText ? props.searchItem.SingleSearchLineText : ''
  );
  const originalUrlRef = useRef(window.location.href);

  // Mirrors the class componentDidUpdate: resync local searchItem when the
  // searchItem prop object changes.
  useEffect(() => {
    setSearchItem({ ...props.searchItem });
  }, [props.searchItem]);

  // Hold the latest values so the once-created debounced handler never reads a
  // stale closure (the class version read this.state/this.props at fire time).
  const latestRef = useRef({ searchItem, singleSearchLineText, clickOnOk: props.clickOnOk });
  latestRef.current = { searchItem, singleSearchLineText, clickOnOk: props.clickOnOk };

  // Created once (empty deps) like the class instance field; reads latest via ref.
  const debouncedSingleSearchLineOnChange = useMemo(
    () =>
      debounce(() => {
        const latest = latestRef.current;
        latest.clickOnOk(ShownPanelsEnum.Search, {
          ...latest.searchItem,
          SingleSearchLineText: latest.singleSearchLineText,
        });
      }, 1500),
    []
  );

  useEffect(() => () => debouncedSingleSearchLineOnChange.cancel(), [debouncedSingleSearchLineOnChange]);

  const singleSearchLineOnChange = (e) => {
    setSingleSearchLineText(e.target.value);
    debouncedSingleSearchLineOnChange();
  };

  const renderSearchPanel = () => {
    if (!showFilter) return null;
    return (
      <GeneralSearchPanel
        searchItem={searchItem}
        searchparamsType={props.searchParamsType}
        clickOnCancel={() => {
          setShowFilter(false);
          props.clickOnCancel();
        }}
        clickOnOk={(key, item) => {
          setShowFilter(false);
          props.clickOnOk(key, item);
        }}
        hideFilter={() => setShowFilter(false)}
        hideAdvancedButton={props.hideAdvancedButton}
        originalUrl={originalUrlRef.current}
        enableUrlPush={props.enableUrlPush}
        clickOnClearAll={props.useClickOnClearAll && props.clickOnClearAll}
      />
    );
  };

  const renderSearch = () => (
    <div className='search'>
      <i
        className='fa-solid fa-magnifying-glass'
        onClick={props.singleSearchLine ? () => setShowFilter(!showFilter) : undefined}
      />
      <input
        placeholder={
          props.singleSearchLine
            ? props.singleSearchLinePlaceholder
              ? props.singleSearchLinePlaceholder
              : 'Search Id, Name and so on...'
            : ''
        }
        value={props.singleSearchLine ? singleSearchLineText : ''}
        onChange={singleSearchLineOnChange}
        onFocus={(e) => e.target.select()}
        disabled={!props.singleSearchLine}
      />
    </div>
  );

  const renderCountPill = () => {
    if (props.resultCount === undefined || props.resultCount === null) return null;
    return (
      <div className='count-pill'>
        <b>{props.resultCount}</b> {props.resultCount === 1 ? 'item' : 'items'}
      </div>
    );
  };

  const renderFilterButton = () => {
    const activeCount = countActiveFilters(searchItem, props.searchParamsType);
    return (
      <button
        type='button'
        className='btn'
        disabled={props.isFilterDisabled}
        onClick={() => setShowFilter(!showFilter)}
      >
        <i className='fa-solid fa-sliders' />
        <span className='btn-label'>Filters</span>
        {activeCount > 0 && <span className='fcount'>{activeCount}</span>}
      </button>
    );
  };

  const renderSettingsButton = () =>
    ButtonForGeneralSearchStrip(
      props.isOptionsDisabled,
      props.clickOnRefresh,
      props.isOptionsDisabled ? null : 'Settings',
      'fa-solid fa-gear',
      null,
      'settings-btn'
    );

  const renderRefreshButton = () =>
    ButtonForGeneralSearchStrip(props.isRefreshDisabled, props.clickOnRefresh, 'Refresh', 'fa-solid fa-arrows-rotate');

  const renderClearAllButton = () =>
    ButtonForGeneralSearchStrip(
      props.isRefreshDisabled,
      props.clickOnClearAll,
      'Clear All Filters',
      'fa-solid fa-eraser',
      null,
      'clear-filters'
    );

  const renderExportExcelButton = () =>
    ButtonForGeneralSearchStrip(
      props.isExportExcelDisabled,
      props.clickOnExportExcel,
      props.excelExportTooltipMessage,
      'fa-regular fa-file-excel',
      props.excelExportRoleKey
    );

  return (
    <div className='bravo-ds'>
      <div className='toolbar'>
        {renderSearch()}
        {renderCountPill()}
        <div className='sp' />
        {renderFilterButton()}
        <div className='tool-group'>
          {renderExportExcelButton()}
          {renderRefreshButton()}
          {renderClearAllButton()}
          <ColumnsMenu
            columns={props.columns}
            hiddenColumns={props.hiddenColumns}
            onToggleColumn={props.onToggleColumn}
            onResetColumns={props.onResetColumns}
          />
          {renderSettingsButton()}
        </div>
      </div>
      {renderSearchPanel()}
    </div>
  );
}

GeneralSearchStrip.defaultProps = {
  searchItem: null,
  clickOnOk: (searchItem) => alert('Please, create an Ok function!'),
  clickOnRefresh: () => alert('Please, create a Refresh function!'),
  clickOnCancel: (searchItem) => alert('Please, create a Cancel function!'),
  clickOnExport: (searchItem) => alert('Please, create a Export function!'),
  clickOnImport: (searchItem) => alert('Please, create a import function!'),
  clickOnExportExcel: (searchItem) => alert('Please, create a export excel function!'),
  searchParamsType: null,
  hideAdvancedButton: false,
  exportFunction: null,
  isRefreshDisabled: true,
  isFilterDisabled: true,
  isOptionsDisabled: true,
  isExportDisabled: true,
  isImportDisabled: true,
  isExportExcelDisabled: true,
  enableUrlPush: true,
  singleSearchLine: false,
  singleSearchLinePlaceholder: 'Search...',
  excelExportRoleKey: null,
  excelExportTooltipMessage: 'Export Excel based on filter',
  useClickOnClearAll: false,
  resultCount: null,
  columns: null,
  hiddenColumns: null,
  onToggleColumn: () => {},
  onResetColumns: () => {},
};
